/**
 * Reporte Consignaciones — migración web de ares9860.
 *
 * Reporte por rango de fechas del negocio de consignaciones: motores
 * consignados (activos y vendidos) y motores devueltos al suplidor, con
 * un resumen general (conteos, costos por estado, días promedio en
 * inventario y tasa de conversión). Exporta a Excel e imprime la pestaña
 * activa.
 */
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx, StatusPill } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const aNumero = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const fmtMoney = (v) =>
  aNumero(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioAnoISO = () => `${new Date().getFullYear()}-01-01`;

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales, numericas) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales, numericas });
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

function estadoPill(estado) {
  const s = String(estado ?? "")
    .trim()
    .toUpperCase();
  if (s === "V") return <StatusPill label="Vendido" variant="info" />;
  if (s === "D") return <StatusPill label="Devuelto" variant="danger" />;
  if (s === "X") return <StatusPill label="Sin inventario" variant="neutral" />;
  if (s === "") return <StatusPill label="Activo" variant="success" />;
  return <StatusPill label={estado || "—"} variant="neutral" />;
}

// Misma etiqueta en pantalla, Excel e impresión: el papel que se cuadra con
// el consignante no puede contar un huérfano como si estuviera en piso.
function etiquetaEstado(estado) {
  const s = String(estado ?? "")
    .trim()
    .toUpperCase();
  if (s === "V") return "Vendido";
  if (s === "D") return "Devuelto";
  if (s === "X") return "Sin inventario";
  return "Activo";
}

export default function ReporteConsignaciones() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tab, setTab] = useState(0);
  const [fecha1, setFecha1] = useState(inicioAnoISO());
  const [fecha2, setFecha2] = useState(hoyISO());

  const [motores, setMotores] = useState([]);
  const [devueltos, setDevueltos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  // El backend siempre aceptó filtrar por consignante; el frontend nunca lo
  // mandaba, así que había que leer todo el reporte para ver uno solo.
  const [consignantes, setConsignantes] = useState([]);
  const [consignante, setConsignante] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { ok, data } = await post("/api/cxp_suplidores", {});
        if (ok) setConsignantes(data.suplidores || []);
      } catch (err) {
        setConsignantes([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/consignaciones_reporte", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        ...(consignante ? { suplidor: consignante.id } : {}),
      });
      if (ok) {
        setMotores(data.motores || []);
        setDevueltos(data.devueltos || []);
        setResumen(data.resumen || null);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitulo = `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`;

  // Separación por estado: disponibles (en piso, incluye los "sin
  // inventario") y vendidos; los devueltos ya vienen aparte del backend.
  const disponibles = motores.filter((m) => m.estado !== "V");
  const vendidos = motores.filter((m) => m.estado === "V");
  const NOMBRE_TAB = ["Disponibles", "Vendidos", "Devueltos"];

  const exportar = () => {
    const stamp = hoyISO();
    let hoja;
    if (tab === 2) {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Reporte Consignaciones — Devueltos"],
        [subtitulo],
        [],
        ["Chasis", "Fact#", "Valor", "Fecha devuelto", "Concepto", "Suplidor"],
        ...devueltos.map((d) => [
          d.chasis,
          d.nfactura,
          d.valor,
          d.fechadevuelto,
          d.concepto,
          d.suplidornombre,
        ]),
      ]);
    } else {
      const lista = tab === 0 ? disponibles : vendidos;
      const encabezados = [
        "Chasis",
        "Marca",
        "Modelo",
        "Color",
        "Costo",
        "Precio",
        "Fecha",
        "Días",
        "Estado",
        "Fact#",
        "Suplidor",
      ];
      const filasHoja = lista.map((m) => [
        m.chasis,
        m.marca,
        m.modelo,
        m.colore,
        m.costo,
        m.precio,
        m.fecha,
        m.dias,
        etiquetaEstado(m.estado),
        m.nfactura,
        m.suplidornombre,
      ]);
      if (tab === 1) {
        encabezados.splice(7, 0, "Fecha venta");
        filasHoja.forEach((r, i) => r.splice(7, 0, lista[i].fechaventa || ""));
      }
      hoja = XLSX.utils.aoa_to_sheet([
        [`Reporte Consignaciones — ${NOMBRE_TAB[tab]}`],
        [subtitulo],
        [],
        encabezados,
        ...filasHoja,
      ]);
    }
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, NOMBRE_TAB[tab]);
    XLSX.writeFile(libro, `ReporteConsignaciones_${stamp}.xlsx`);
  };

  const imprimir = () => {
    if (tab === 2) {
      const totalValor = devueltos.reduce((s, d) => s + aNumero(d.valor), 0);
      imprimirListado(
        "Reporte Consignaciones — Devueltos",
        subtitulo,
        ["Chasis", "Fact#", "Valor", "Fecha devuelto", "Concepto", "Suplidor"],
        devueltos.map((d) => [
          d.chasis,
          d.nfactura,
          fmtMoney(d.valor),
          d.fechadevuelto,
          d.concepto,
          d.suplidornombre,
        ]),
        ["", "TOTAL COSTO", fmtMoney(totalValor), "", "", ""],
        [2]
      );
    } else {
      const lista = tab === 0 ? disponibles : vendidos;
      const totalCosto = lista.reduce((s, m) => s + aNumero(m.costo), 0);
      const encabezados = [
        "Chasis",
        "Marca",
        "Modelo",
        "Color",
        "Costo",
        "Precio",
        "Fecha",
        "Días",
        "Estado",
        "Fact#",
        "Suplidor",
      ];
      const filasImp = lista.map((m) => [
        m.chasis,
        m.marca,
        m.modelo,
        m.colore,
        fmtMoney(m.costo),
        fmtMoney(m.precio),
        m.fecha,
        m.dias,
        etiquetaEstado(m.estado),
        m.nfactura,
        m.suplidornombre,
      ]);
      const totales = ["", "", "", "TOTAL COSTO", fmtMoney(totalCosto), "", "", "", "", "", ""];
      if (tab === 1) {
        encabezados.splice(7, 0, "Fecha venta");
        filasImp.forEach((r, i) => r.splice(7, 0, lista[i].fechaventa || ""));
        totales.push("");
      }
      imprimirListado(
        `Reporte Consignaciones — ${NOMBRE_TAB[tab]}`,
        subtitulo,
        encabezados,
        filasImp,
        totales,
        tab === 1 ? [4, 5, 8] : [4, 5, 7]
      );
    }
  };

  const columnasMotores = [
    { field: "chasis", headerName: "Chasis", width: 160 },
    { field: "marca", headerName: "Marca", width: 110 },
    { field: "modelo", headerName: "Modelo", width: 110 },
    { field: "colore", headerName: "Color", width: 95 },
    moneyCol("costo", "Costo"),
    moneyCol("precio", "Precio"),
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "dias", headerName: "Días", width: 70, align: "right", headerAlign: "right" },
    {
      field: "estado",
      headerName: "Estado",
      width: 110,
      renderCell: (params) => estadoPill(params.value),
    },
    { field: "nfactura", headerName: "Fact#", width: 85 },
    { field: "suplidornombre", headerName: "Suplidor", flex: 1, minWidth: 150 },
  ];

  // Vendidos: misma tabla más la fecha de venta (el eje del filtro para
  // este estado), entre la fecha de entrada y los días piso→venta.
  const columnasVendidos = [
    ...columnasMotores.slice(0, 7),
    { field: "fechaventa", headerName: "Fecha venta", width: 105 },
    ...columnasMotores.slice(7),
  ];

  const columnasDevueltos = [
    { field: "chasis", headerName: "Chasis", width: 170 },
    { field: "nfactura", headerName: "Fact#", width: 90 },
    moneyCol("valor", "Valor"),
    { field: "fechadevuelto", headerName: "Fecha devuelto", width: 125 },
    { field: "concepto", headerName: "Concepto", flex: 1, minWidth: 200 },
    { field: "suplidornombre", headerName: "Suplidor", width: 180 },
  ];

  let listaTab = devueltos;
  if (tab === 0) listaTab = disponibles;
  else if (tab === 1) listaTab = vendidos;
  const hayDatos = listaTab.length > 0;

  let columnasTab = columnasDevueltos;
  if (tab === 0) columnasTab = columnasMotores;
  else if (tab === 1) columnasTab = columnasVendidos;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Reporte Consignaciones"
        subtitle="Motores consignados, vendidos y devueltos por rango de fechas"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <Autocomplete
                options={consignantes}
                value={consignante}
                onChange={(_e, v) => setConsignante(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Consignante (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="ACTIVOS" value={String(resumen?.activos ?? 0)} color="#2E7D32" />
            <ResumenChip
              label="VENDIDOS"
              value={String(resumen?.vendidos ?? 0)}
              color={FP.color.accentBlue}
            />
            <ResumenChip
              label="DEVUELTOS"
              value={String(resumen?.devueltos ?? 0)}
              color="#C62828"
            />
            <ResumenChip
              label="COSTO ACTIVOS"
              value={`RD$ ${fmtMoney(resumen?.costoactivos)}`}
              color="#2E7D32"
            />
            <ResumenChip
              label="COSTO VENDIDOS"
              value={`RD$ ${fmtMoney(resumen?.costovendidos)}`}
              color={FP.color.accentBlue}
            />
            <ResumenChip
              label="COSTO DEVUELTOS"
              value={`RD$ ${fmtMoney(resumen?.costodevueltos)}`}
              color="#C62828"
            />
            <ResumenChip label="DÍAS PROMEDIO" value={String(resumen?.diaspromedio ?? 0)} />
            <ResumenChip
              label="TASA CONVERSIÓN"
              value={`${resumen?.tasaconversion ?? 0}%`}
              color="#F9A825"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
          >
            <Tab label={`Disponibles (${disponibles.length})`} />
            <Tab label={`Vendidos (${vendidos.length})`} />
            <Tab label={`Devueltos (${devueltos.length})`} />
          </Tabs>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={listaTab}
              columns={columnasTab}
              getRowId={tab === 2 ? (r) => `${r.chasis}-${r.nfactura}` : (r) => r.chasis}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
